import axios from "axios";
import { Message } from "element-ui";
import store from "@/store";
// import { getRoleToken } from "@/utils/auth";
import router from "@/router";
import { refresh } from "@/api/auth";

const whiteList = [`Authenticate`, `/api/profile/me/`];
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API
    ? process.env.VUE_APP_BASE_API
    : process.env.VUE_APP_PUBLIC_PATH,
  withCredentials: true,
});

// Один общий запрос обновления access-токена на все параллельные 401,
// чтобы не плодить запросы и не зацикливаться.
let refreshTokenPromise = null;

function runRefresh() {
  if (!refreshTokenPromise) {
    refreshTokenPromise = refresh().finally(() => {
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
}

// Разлогин с редиректом на /login. Защищён флагом, чтобы пачка параллельных
// 401 (после общего неудачного refresh) не показала стопку одинаковых тостов
// и не дёргала router.push несколько раз. Флаг снимается после завершения
// редиректа, поэтому последующее истечение сессии снова отработает корректно.
let isLoggingOut = false;

function logoutAndRedirect() {
  if (isLoggingOut) {
    return;
  }
  isLoggingOut = true;

  store
    .dispatch("user/resetToken")
    .then(() => {
      const { currentRoute } = router;

      if (currentRoute.name !== `login`) {
        router.push({
          name: `login`,
          query: { redirect: currentRoute.path },
        });
      }

      Message({
        message: `Вы вышли из системы, войдите снова.`,
        type: "warning",
        duration: 5e3,
      });
    })
    .finally(() => {
      isLoggingOut = false;
    });
}

service.interceptors.request.use(
  (config) => {
    // config.headers["X-Authorization"] = getRoleToken(); // <<<--- test Token
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

service.interceptors.response.use(
  (response) => {
    if (response.status === 201) {
      const reportMessage =
        response.data && response.data.token
          ? ` Отчет будет доступен по id ${response.data.token}`
          : ``;

      Message({
        message: `Данные обрабатываются.${reportMessage}`,
        type: `info`,
        duration: 8e3,
      });
    }
    return response.data;
  },
  async (error) => {
    const originalRequest = (error && error.config) || {};

    // Сам запрос обновления токена обрабатываем тихо: без тостов и редиректов —
    // решение о разлогине принимает перехватчик исходного запроса.
    if (originalRequest._refresh) {
      return Promise.reject(error);
    }

    if (!error || !error.response) {
      Message({
        message: error?.message || "Ошибка сети",
        type: "error",
        duration: 5e3,
      });
      return Promise.reject(error);
    }

    const { status, data } = error.response;
    const url = originalRequest.url || "";
    // Тело ошибки не всегда объект (пустой ответ, текст шлюза, null) —
    // читаем поля безопасно, чтобы не уронить перехватчик деструктуризацией.
    const body = data && typeof data === "object" ? data : {};
    const detail = body.detail !== undefined ? body.detail : "";
    const error_description = body.error_description || "";

    const reqAuth = whiteList.some((elem) => url.includes(elem));
    const isTokenEndpoint = url.includes("/api/oauth/token");
    const isAccessDenied =
      detail?.code === "ACCESS_DENIED" && body.error === "access_denied";

    // Пред-авторизационная проба (skipRefresh): её 401 отдаём молча —
    // обновлять и редиректить не нужно, решение принимает вызывающий код.
    if (status === 401 && originalRequest._skipRefresh) {
      return Promise.reject(error);
    }

    // Access-токен истёк (401): обновляем его по refresh-cookie и повторяем
    // исходный запрос. Не трогаем эндпоинты авторизации (login/refresh/logout),
    // уже повторённые запросы и явный отказ доступа, чтобы не зациклиться.
    if (
      status === 401 &&
      !isTokenEndpoint &&
      !originalRequest._retry &&
      !isAccessDenied
    ) {
      originalRequest._retry = true;

      try {
        await runRefresh();
        // ВАЖНО: именно `return service(...)`, а НЕ `return await`. Без await
        // ошибка повтора (500/422/сеть) уходит вызывающему как есть и не
        // попадает в catch ниже — иначе валидную обновлённую сессию ложно
        // разлогинило бы. Повтор любого метода безопасен: 401 означает, что
        // запрос отклонён до обработки на бэке, побочный эффект не применялся.
        return service(originalRequest);
      } catch (refreshError) {
        // Обновить не удалось — сессия истекла, обрабатываем как разлогин ниже.
      }

      // На whitelist-эндпоинтах (me) редирект делает route guard — не дублируем.
      if (!reqAuth) {
        logoutAndRedirect();
      }

      return Promise.reject(error);
    }

    if (status === 401 && !reqAuth && !isAccessDenied) {
      logoutAndRedirect();
      return Promise.reject(error);
    }

    if (status === 400 && body.error === "TwoFactorRequired" && body.message) {
      Message({
        message: body.message,
        type: "success",
        duration: 5e3,
      });
      return Promise.reject(error);
    }

    if (status === 422) {
      Message({
        message: `Ошибка валидации, проверьте данные.`,
        type: "warning",
        duration: 5e3,
      });
      return Promise.reject(data);
    }

    if (status === 412) {
      return Promise.reject(data);
    }
    const respMessage = detail?.message || body.message || error_description;

    if (respMessage) {
      Message({
        message: respMessage,
        type: "error",
        duration: 5e3,
      });
    } else {
      Message({
        message: detail
          ? `${
              typeof detail === "object" ? JSON.stringify(detail) : detail
            } (${status})`
          : error.message,
        type: "error",
        duration: 5e3,
      });
    }
    return Promise.reject(error);
  }
);

export default service;
